import React, { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import GeneralInfo from "./form-sections/GeneralInfo";
import ApiConfig from "./form-sections/ApiConfig";
import RoutingLimits from "./form-sections/RoutingLimits";

export default function ProviderForm({ provider, open, onClose, onSave }) {
  const { t } = useLanguage();
  const defaultValues = {
    name: "",
    code: "",
    type: "bank",
    status: "active",
    priority: 1,
    weight: 100,
    health_score: 100,
    success_rate: 100,
    avg_response_time: 0,
    supports_deposit: true,
    supports_withdrawal: true,
    supports_qr: false,
    min_amount: 1,
    max_amount: 1000000,
    daily_limit: 50000000,
    current_daily_volume: 0,
    api_endpoint: "",
    api_key: "",
    api_secret: "",
    webhook_secret: "",
    ip_whitelist: [],
    fee_percentage: 1.5,
    settlement_time: "instant",
    logo_url: "",
    notes: "",
  };

  const getFormData = (provider) => ({
    ...defaultValues,
    ...provider,
    status: provider?.status?.toLowerCase() ?? "active",
    type: provider?.type?.toLowerCase() ?? "bank",
  });

  const [formData, setFormData] = React.useState(() => getFormData(provider));

  React.useEffect(() => {
    if (open) setFormData(getFormData(provider));
  }, [provider, open]);

  const [newIp, setNewIp] = React.useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addIp = () => {
    if (newIp && !formData.ip_whitelist?.includes(newIp)) {
      handleChange("ip_whitelist", [...(formData.ip_whitelist || []), newIp]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => {
    handleChange(
      "ip_whitelist",
      formData.ip_whitelist?.filter((i) => i !== ip),
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave({
      ...formData,
      status: formData.status.toUpperCase(),
    });
    setSaving(false);
  };
  const tabs = [
    { value: "general", label: t("general") },
    { value: "api", label: t("apiConfig") },
    { value: "routing", label: t("routingLimits") },
  ];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {provider ? t("editProvider") : t("addNewProvider")}
            {provider && (
              <Badge variant="secondary" className="font-mono text-xs">
                {provider.code}
              </Badge>
            )}
          </DialogTitle>
          {provider && (
            <p className="text-sm text-slate-500">
              Editing: {provider.nameEn || provider.name}
            </p>
          )}
        </DialogHeader>

        <Tabs defaultValue="general" className="mt-4">
          <TabsList className={`grid grid-cols-${tabs.length} w-full`}>
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="general">
            <GeneralInfo
              formData={formData}
              handleChange={handleChange}
              t={t}
            />
          </TabsContent>

          <TabsContent value="api">
            <ApiConfig
              formData={formData}
              handleChange={handleChange}
              t={t}
              newIp={newIp}
              setNewIp={setNewIp}
              addIp={addIp}
              removeIp={removeIp}
            />
          </TabsContent>

          <TabsContent value="routing">
            <RoutingLimits
              formData={formData}
              handleChange={handleChange}
              t={t}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving
              ? t("saving")
              : provider
                ? t("updateProvider")
                : t("addProviderBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
